import type { PostHogConfig } from 'posthog-js/dist/module.slim.no-external';
import type { RouteObject } from 'react-router-dom';

import { type UsageConfig, getUsageConfig, usageId } from '@/usage/config';
import { type ReplayExtensions, loadReplay, usageReplayOptions } from '@/usage/replay';
import {
  type UsageEventName,
  type UsageEventProps,
  type UsageRecord,
  cleanUsageProps,
  isUsageEventName,
} from '@/usage/events';
import {
  GROUP_TYPE,
  type OutgoingEvent,
  type ToPattern,
  createBeforeSend,
  maskPath,
  patternUrl,
  routePattern,
} from '@/usage/scrub';

/**
 * Usage analytics client (PostHog EU cloud), behind the switch of
 * `usage/config.ts`. While the switch is off every function here returns at
 * once: the SDK is not imported and nothing is sent.
 *
 * Privacy (owner 2026-10-12):
 * - the SDK is loaded (dynamic `import()`, its own chunk) only when the switch
 *   is on AND a user is signed in with a confirmed role; nothing is sent before;
 * - the person is the numeric user id; the group is the organisation (access
 *   context id) with its type; the active role assignment id is a property.
 *   No e-mail, no name, no organisation / brand / partner name;
 * - one PostHog project for all environments: every event carries
 *   `environment` (the build's VITE_APP_ENV), and outside production the
 *   person and organisation ids are prefixed with it (`dev:812`, `dev:63`) so
 *   they never merge with production ones (see `usage/config.ts`);
 * - `persistence: 'memory'`: no cookie, nothing kept in localStorage or
 *   sessionStorage (a page reload starts a new analytics session). At start-up
 *   the SDK probes localStorage once: it writes the constant test key
 *   `__mplssupport__` and removes it in the same call (no setting turns that off);
 * - no autocapture, no surveys, no feature flags and no remote configuration:
 *   the bundle used (`module.slim.no-external`) contains none of those
 *   extensions and can not load a script from elsewhere;
 * - session replay only with its own switch (`VITE_POSTHOG_REPLAY=on`), masked
 *   (all text, inputs and text-bearing attributes; images, canvas and SVG
 *   blocked; no network or console): see `usage/replay.ts`. Its recorder is
 *   bundled with the app and loaded only then;
 * - every event passes `usage/scrub.ts` (allow-list, route patterns, e-mail / JWT guard);
 * - sign-out and a role switch reset the SDK (new anonymous id, device id and
 *   session; the new role is identified afresh).
 */

/** Who is signed in and under which role: ids and one enum value only. */
export interface UsageIdentity {
  userId: number;
  assignmentId: number;
  /** the organisation the role belongs to (access context id); null when the role has none */
  organisationId: number | null;
  /** INTERNAL | BRAND | RIGHT_HOLDER | AGENCY */
  organisationType: string;
}

/** The part of the SDK this module uses. */
export interface UsageSdk {
  init: (key: string, options: Partial<PostHogConfig>) => unknown;
  identify: (distinctId: string) => void;
  group: (type: string, key: string, properties?: Record<string, unknown>) => void;
  register: (properties: Record<string, unknown>) => void;
  capture: (event: string, properties?: Record<string, unknown>) => unknown;
  reset: (resetDeviceId?: boolean) => void;
}

/**
 * The SDK options. One place, so the tests pin every privacy setting.
 * Replay is added only when the config asks for it AND its extension was loaded.
 */
export const usageInitOptions = (
  config: UsageConfig,
  beforeSend: (event: OutgoingEvent | null) => OutgoingEvent | null,
  replay?: { toPattern: ToPattern; extensions: ReplayExtensions } | null,
): Partial<PostHogConfig> => ({
  api_host: config.host,
  ui_host: config.uiHost,
  // nothing on the device
  persistence: 'memory',
  // only what we send ourselves
  autocapture: false,
  rageclick: false,
  capture_pageview: false,
  capture_pageleave: true,
  capture_performance: false,
  capture_heatmaps: false,
  capture_dead_clicks: false,
  capture_exceptions: false,
  disable_scroll_properties: true,
  save_campaign_params: false,
  mask_personal_data_properties: true,
  // no recording, surveys, tours, chat, experiments
  disable_session_recording: true,
  disable_surveys: true,
  disable_product_tours: true,
  disable_conversations: true,
  disable_web_experiments: true,
  // no calls besides the events: no feature flags, no remote configuration, no extra scripts
  advanced_disable_flags: true,
  advanced_disable_feature_flags: true,
  advanced_disable_toolbar_metrics: true,
  disable_external_dependency_loading: true,
  person_profiles: 'identified_only',
  // In memory every page load starts with a new anonymous id. It is not sent
  // with the identify, so the ids are not merged onto the person one by one.
  reuseAnonymousId: true,
  respect_dnt: true,
  // No effect in this SDK version (kept as the stated intent): the IP address is
  // discarded by the PostHog project setting and by the forward on our web server.
  ip: false,
  before_send: beforeSend as unknown as PostHogConfig['before_send'],
  ...(config.replay && replay
    ? { ...usageReplayOptions(replay.toPattern), __extensionClasses: replay.extensions }
    : {}),
});

let sdk: Promise<UsageSdk | null> | null = null;
let identity: UsageIdentity | null = null;
let queue: Promise<void> = Promise.resolve();
let toPattern: ToPattern = maskPath;

const load = (config: UsageConfig): Promise<UsageSdk | null> => {
  const page: ToPattern = (path) => toPattern(path);
  sdk ??= Promise.all([
    import('posthog-js/dist/module.slim.no-external'),
    // without its recorder the page still sends events, only no replay
    config.replay ? loadReplay(config.key).catch(() => null) : null,
  ])
    .then(([module, extensions]) => {
      const client = module.default as unknown as UsageSdk;
      const replay = extensions ? { toPattern: page, extensions } : null;
      client.init(
        config.key,
        usageInitOptions(config, createBeforeSend(page, { replay: replay !== null }), replay),
      );
      return client;
    })
    .catch(() => {
      console.warn('Usage analytics could not be loaded; it stays off on this page.');
      return null;
    });
  return sdk;
};

/** Runs `step` on the SDK after everything queued before it (the order of calls is kept). */
const run = (config: UsageConfig, step: (client: UsageSdk) => void): void => {
  queue = queue
    .then(() => load(config))
    .then((client) => {
      if (client) step(client);
    })
    .catch(() => undefined);
};

const sameIdentity = (a: UsageIdentity | null, b: UsageIdentity | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.userId === b.userId &&
    a.assignmentId === b.assignmentId &&
    a.organisationId === b.organisationId &&
    a.organisationType === b.organisationType);

/** The routes of the app (src/routes), so addresses are sent as their route pattern. */
export const setUsageRoutes = (routes: RouteObject[] | null): void => {
  toPattern = routes ? (path) => routePattern(routes, path) : maskPath;
};

/**
 * Follow the session: `next` is the signed-in user with the active role, or
 * null (signed out, or the role is not confirmed yet). A change of user or
 * role resets the SDK first, so nothing of the previous one carries over.
 */
export const syncUsageSession = (next: UsageIdentity | null): void => {
  const config = getUsageConfig();
  if (!config || sameIdentity(identity, next)) return;
  const previous = identity;
  identity = next;
  if (previous) run(config, (client) => client.reset(true));
  if (!next) return;
  run(config, (client) => {
    client.register({
      environment: config.environment,
      role_assignment_id: next.assignmentId,
      organisation_type: next.organisationType,
    });
    client.identify(usageId(config.environment, next.userId));
    if (next.organisationId !== null) {
      client.group(GROUP_TYPE, usageId(config.environment, next.organisationId), {
        organisation_type: next.organisationType,
      });
    }
  });
};

/**
 * A page view of the route with pattern `pattern` (`/brandprofile/:id`); on a
 * detail page with its record (`record_type` + numeric `record_id`, usage/records.ts).
 */
export const trackUsagePageView = (pattern: string, record?: UsageRecord | null): void => {
  const config = getUsageConfig();
  if (!config || !identity) return;
  const properties = {
    $current_url: patternUrl(pattern),
    $pathname: pattern,
    ...cleanUsageProps(record),
  };
  run(config, (client) => client.capture('$pageview', properties));
};

/** One of the named events (usage/events.ts) with its allowed properties. */
export const trackUsage = <N extends UsageEventName>(name: N, props?: UsageEventProps[N]): void => {
  const config = getUsageConfig();
  if (!config || !identity || !isUsageEventName(name)) return;
  const properties = cleanUsageProps(props);
  run(config, (client) => client.capture(name, properties));
};

/** Test helpers: wait for everything queued; forget the page's analytics state. */
export const flushUsageForTests = (): Promise<void> => queue;

export const resetUsageForTests = (): void => {
  sdk = null;
  identity = null;
  queue = Promise.resolve();
  toPattern = maskPath;
};
